import React from 'react';
import { Navigate } from 'react-router-dom';
import MainLayout from '../layout/MainLayout';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * A wrapper for protected routes that checks authentication and dynamic permissions.
 * 
 * @param {Object} props
 * @param {Object} props.user - The Firebase user object
 * @param {Object} props.userData - The extended user data from Firestore
 * @param {string} props.requiredPermission - The permission key required to access this route (from PERMISSIONS constant)
 * @param {string} [props.requiredAction='view'] - The specific action required (default: 'view')
 * @param {Array<string>} [props.roles] - Fallback: List of allowed roles if no permission is specified
 * @param {React.ReactNode} props.children - The component to render
 */
const ProtectedRoute = ({ user, userData, children, requiredPermission, requiredAction = 'view', roles = [] }) => {
    // We use the hook here to get fresh permission logic
    // make sure passed userData has the tenantId populated for the hook to work
    const { can, loading, isOwner } = usePermissions(userData);

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // If still loading permissions, wait before checking redirects
    // effectively 'can' returns false if loading, so we must wait
    if (loading) {
        return <div className="flex justify-center items-center h-screen"><div className="badge badge-pending">Yetkiler Kontrol Ediliyor...</div></div>;
    }

    // 1. Dynamic Permission Check (Priority)
    if (requiredPermission) {
        if (!can(requiredPermission, requiredAction)) {
            // Optional: Redirect to a standardized "Access Denied" page
            // For now, redirect to dashboard or Show filtered dashboard
            return <Navigate to="/" replace />;
        }
    }
    // 2. Role-Based Check (Fallback/Legacy)
    else if (roles.length > 0) {
        if (!roles.includes(userData?.role)) {
            return <Navigate to="/" replace />;
        }
    }

    // If passes checks
    return (
        <MainLayout user={user} userData={userData}>
            {children}
        </MainLayout>
    );
};

export default ProtectedRoute;
